// @ts-nocheck
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { URL } from '../../service/Service';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid';
import AuthShell from '../../components/auth/AuthShell';
import { userFriendlyError } from '../../utils/userFriendlyError';

const utf8Length = (value) => Array.from(value).reduce((total, character) => {
    const codePoint = character.codePointAt(0);
    if (codePoint <= 0x7f) return total + 1;
    if (codePoint <= 0x7ff) return total + 2;
    if (codePoint <= 0xffff) return total + 3;
    return total + 4;
}, 0);

const getPasswordStrength = (value) => [
    value.length >= 8 && utf8Length(value) <= 72,
    /[A-Z]/.test(value),
    /[a-z]/.test(value),
    /\d/.test(value),
    /[^A-Za-z0-9]/.test(value),
].filter(Boolean).length;

const ResetPasswordForm = () => {
    const { token } = useParams(); // Extract the token from the URL
    const [userEmail, setUserEmail] = useState('');
    const [userPass, setUserPass] = useState('');
    const [userPass2, setUserPass2] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const passwordStrength = getPasswordStrength(userPass);
    const passwordsMatch = Boolean(userPass2) && userPass === userPass2;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,72}$/.test(userPass)
                || utf8Length(userPass) > 72) {
            setError('A palavra-passe deve ter entre 8 e 72 caracteres, maiúscula, minúscula, número e símbolo.');
            setMessage('');
            return;
        }
        if (userPass !== userPass2) {
            setError('As palavras-passe não coincidem.');
            setMessage('');
            return;
        }
        try {
            setSubmitting(true);
            const response = await axios.patch(`${URL}/api/users/resetpassword`, {
                email: userEmail.trim().toLowerCase(),
                newPassword: userPass,
                token: token
            });
            setMessage(response.data.message || 'Palavra-passe alterada com sucesso');
            setError('');

            // Redirect to /login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (error) {
            setError(userFriendlyError(
                error?.response?.data,
                'Não foi possível alterar a palavra-passe. Tente novamente.',
            ));
            setMessage('');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthShell eyebrow="Segurança da conta" title="Definir nova palavra-passe" description="Escolha uma palavra-passe forte e diferente das que utilizou anteriormente.">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label htmlFor="email" className="ui-label">
                                Email
                            </label>
                            <div className="mt-1">
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={userEmail}
                                    onChange={e => setUserEmail(e.target.value)}
                                    className="ui-input"
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="password" className="ui-label">
                                Nova palavra-passe
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="password"
                                    name="password"
                                    autoComplete="new-password"
                                    type={passwordVisible ? "text" : "password"}
                                    required
                                    value={userPass}
                                    onChange={e => setUserPass(e.target.value)}
                                    className="ui-input pr-12"
                                />
                                <button
                                    type="button"
                                    onClick={() => setPasswordVisible(!passwordVisible)}
                                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl text-green-cl hover:bg-emerald-50"
                                    aria-label={passwordVisible ? 'Ocultar palavra-passe' : 'Mostrar palavra-passe'}
                                >
                                    {passwordVisible ? <EyeSlashIcon className="h-5 w-5 text-gray-500" /> : <EyeIcon className="h-5 w-5 text-gray-500" />}
                                </button>
                            </div>
                            <p className="mt-2 text-xs leading-5 text-slate-500">
                                Entre 8 e 72 caracteres, com maiúscula, minúscula, número e símbolo.
                            </p>
                            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200"
                                 aria-label={`${passwordStrength} de 5 requisitos de segurança cumpridos`}>
                                <div
                                    className={`h-full rounded-full transition-all duration-300 ${passwordStrength === 5 ? 'bg-emerald-600' : 'bg-amber-500'}`}
                                    style={{width: `${passwordStrength * 20}%`}}
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="password2" className="ui-label">
                                Repetir nova palavra-passe
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="password2"
                                    name="password2"
                                    autoComplete="new-password"
                                    type={confirmPasswordVisible ? "text" : "password"}
                                    required
                                    value={userPass2}
                                    onChange={e => setUserPass2(e.target.value)}
                                    className="ui-input pr-12"
                                />
                                <button
                                    type="button"
                                    onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl text-green-cl hover:bg-emerald-50"
                                    aria-label={confirmPasswordVisible ? 'Ocultar confirmação' : 'Mostrar confirmação'}
                                >
                                    {confirmPasswordVisible ? <EyeSlashIcon className="h-5 w-5 text-gray-500" /> : <EyeIcon className="h-5 w-5 text-gray-500" />}
                                </button>
                            </div>
                            {userPass2 && (
                                <p className={`mt-2 text-xs font-semibold ${passwordsMatch ? 'text-emerald-700' : 'text-rose-600'}`}>
                                    {passwordsMatch ? 'As palavras-passe coincidem.' : 'As palavras-passe não coincidem.'}
                                </p>
                            )}
                        </div>
                        <p className="text-sm text-slate-500">
                            Se o link expirou, <a href="/resetpassword" className="auth-link">pedir novo link</a>.
                        </p>
                        <div>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="ui-button-primary w-full"
                            >
                                {submitting ? 'A alterar palavra-passe…' : 'Alterar palavra-passe'}
                            </button>
                        </div>
                        {message && <div className="ui-alert-success text-center">{message}</div>}
                        {error && <div className="ui-alert-error text-center">{error}</div>}
                        <div className="text-sm text-center">
                            <a href="/login" className="auth-link">
                                Voltar à página de login
                            </a>
                        </div>
                    </form>
        </AuthShell>
    );
}

export default ResetPasswordForm;
